import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

import { supabase } from '@/lib/customSupabaseClient';
import { useToast } from '@/components/ui/use-toast';
import { securityService } from '@/lib/securityService';
import { getDeviceInfo, getDeviceFingerprint, getLocationData } from '@/lib/deviceUtils';

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const { toast } = useToast();

  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTenant, setCurrentTenant] = useState(null);
  const [deviceSession, setDeviceSession] = useState(null);
  const [securityStatus, setSecurityStatus] = useState({
    requiresDeviceVerification: false,
    blockedDevices: [],
    suspiciousActivity: false
  });

  const buildProfileFallback = useCallback((authUser) => ({
    id: authUser.id,
    email: authUser.email,
    role:
      authUser.app_metadata?.role ||
      authUser.user_metadata?.role ||
      'technician',
    full_name: authUser.user_metadata?.full_name || authUser.email || 'User',
  }), []);

  // Enhanced profile fetching with timeout so login never hangs indefinitely
  const fetchUserProfile = useCallback(async (userId) => {
    if (!userId) return null;

    const fetchPromise = supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();

    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Profile fetch timeout')), 10000);
    });

    try {
      const { data, error } = await Promise.race([fetchPromise, timeoutPromise]);
      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }
      return data;
    } catch (error) {
      console.error('Profile fetch error:', error);
      return null;
    }
  }, []);

  // Enhanced session handling with profile management and security
  const handleSession = useCallback(async (session) => {
    setLoading(true);
    setSession(session);
    setUser(session?.user ?? null);

    try {
      if (session?.user) {
        const userProfile = await fetchUserProfile(session.user.id);
        const resolvedProfile = userProfile || buildProfileFallback(session.user);
        setProfile(resolvedProfile);

        if (resolvedProfile?.tenant_id) {
          const { data: tenantRow } = await supabase
            .from('tenants')
            .select('*')
            .eq('id', resolvedProfile.tenant_id)
            .single();
          if (tenantRow) {
            setCurrentTenant(tenantRow);
          }
        }

        const currentDeviceSession = securityService.getCurrentSession();
        if (currentDeviceSession) {
          setDeviceSession(currentDeviceSession);
        }
      } else {
        setProfile(null);
        setCurrentTenant(null);
        setDeviceSession(null);
        setSecurityStatus({
          requiresDeviceVerification: false,
          blockedDevices: [],
          suspiciousActivity: false
        });
      }
    } catch (error) {
      console.warn('Session handling failed:', error);
      if (session?.user) {
        setProfile(buildProfileFallback(session.user));
      }
    } finally {
      setLoading(false);
    }
  }, [fetchUserProfile, buildProfileFallback]);

  useEffect(() => {
    let timeoutId;
    let mounted = true;
    
    const getSession = async () => {
      try {
        timeoutId = setTimeout(() => {
          if (mounted) {
            console.warn('Session loading timeout — check Supabase connectivity');
            setLoading(false);
          }
        }, 15000);

        const { data: { session } } = await supabase.auth.getSession();

        if (mounted) {
          clearTimeout(timeoutId);
          handleSession(session);
        }
      } catch (error) {
        console.warn('Failed to get session:', error);
        if (mounted) {
          clearTimeout(timeoutId);
          setLoading(false);
        }
      }
    };

    // Start session check immediately
    getSession();

    let subscription;
    try {
      const { data } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          if (mounted) {
            handleSession(session);
          }
        }
      );
      subscription = data.subscription;
    } catch (error) {
      console.warn('Failed to set up auth state listener:', error);
      if (mounted) setLoading(false);
    }

    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      if (subscription) subscription.unsubscribe();
    };
  }, [handleSession]);

  // Enhanced sign up with profile creation
  const signUp = useCallback(async (email, password, options = {}) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options,
      });

      if (error) {
        toast({
          variant: "destructive",
          title: "Sign up Failed",
          description: error.message || "Something went wrong",
        });
        return { data: null, error };
      }

      // Create user profile if signup successful
      if (data?.user) {
        try {
          const { error: profileError } = await supabase
            .from('user_profiles')
            .insert([
              {
                id: data.user.id,
                email: data.user.email,
                full_name: options?.data?.full_name || '',
                role: options?.data?.role || 'technician',
                avatar_url: null,
              }
            ]);

          if (profileError) {
            console.error('Profile creation error:', profileError);
          }
        } catch (profileError) {
          console.warn('Profile creation failed, continuing:', profileError);
        }
      }

      return { data, error: null };
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Sign up Failed",
        description: "An unexpected error occurred",
      });
      return { data: null, error };
    }
  }, [toast]);

  // Enhanced sign in with proper error handling
  const signIn = useCallback(async (email, password, options = {}) => {
    const { silent = false } = options;
    try {
      // Try Supabase authentication first
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        if (!silent) {
          toast({
            variant: "destructive",
            title: "Sign in Failed",
            description: error.message || "Authentication failed",
          });
        }
        return { data: null, error };
      }

      // Update auth state immediately (mock client does not trigger onAuthStateChange)
      if (data?.session) {
        await handleSession(data.session);
      }
      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      toast({
        variant: "destructive",
        title: "Sign in Failed",
        description: "An unexpected error occurred",
      });
      return { data: null, error };
    }
  }, [toast, handleSession]);

  // Enhanced sign out with security
  const signOut = useCallback(async () => {
    try {
      // Logout from security service if device session exists
      if (deviceSession?.session_token) {
        await securityService.logout(deviceSession.session_token);
      }

      const { error } = await supabase.auth.signOut();

      if (error) {
        console.warn('Supabase signout failed:', error);
      }

      // Clear local state regardless
      setUser(null);
      setSession(null);
      setProfile(null);
      setCurrentTenant(null);
      setDeviceSession(null);
      setSecurityStatus({
        requiresDeviceVerification: false,
        blockedDevices: [],
        suspiciousActivity: false
      });
      
      toast({
        title: "Signed Out",
        description: "You have been signed out successfully",
      });
      
      return { error: null };
    } catch (error) {
      console.warn('Sign out error:', error);
      // Clear state anyway
      setUser(null);
      setSession(null);
      setProfile(null);
      return { error: null };
    }
  }, [toast, deviceSession]);

  // Tenant management functions
  const switchTenant = useCallback(async (tenantId) => {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select('*')
        .eq('id', tenantId)
        .single();

      if (error) {
        toast({
          variant: "destructive",
          title: "Tenant Switch Failed",
          description: error.message || "Unable to switch tenant",
        });
        return { error };
      }

      // Persist the active tenant so RLS (current_tenant_id) reflects the switch,
      // otherwise the UI switches but the data stays scoped to the old tenant.
      if (user?.id) {
        const { error: persistError } = await supabase
          .from('user_profiles')
          .update({ tenant_id: tenantId })
          .eq('id', user.id);
        if (persistError) {
          toast({
            variant: "destructive",
            title: "Tenant Switch Failed",
            description: persistError.message || "Unable to switch tenant",
          });
          return { error: persistError };
        }
        setProfile((prev) => (prev ? { ...prev, tenant_id: tenantId } : prev));
      }

      setCurrentTenant(data);
      toast({
        title: "Tenant Switched",
        description: `Now managing ${data.name || data.company_name || 'tenant'}`,
        variant: "success",
      });

      return { data, error: null };
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Tenant Switch Failed",
        description: "An unexpected error occurred",
      });
      return { error };
    }
  }, [toast, user]);

  const refreshBranding = useCallback(async () => {
    if (user?.id) {
      const userProfile = await fetchUserProfile(user.id);
      if (userProfile) setProfile(userProfile);
    }
    const tenantId = profile?.tenant_id;
    if (tenantId) {
      const { data: tenantRow } = await supabase
        .from('tenants')
        .select('*')
        .eq('id', tenantId)
        .single();
      if (tenantRow) setCurrentTenant(tenantRow);
    }
  }, [user, profile?.tenant_id, fetchUserProfile]);

  const value = useMemo(() => ({
    user,
    session,
    profile,
    loading,
    currentTenant,
    deviceSession,
    securityStatus,
    signUp,
    signIn,
    signOut,
    switchTenant,
    fetchUserProfile,
    refreshBranding,
  }), [user, session, profile, loading, currentTenant, deviceSession, securityStatus, signUp, signIn, signOut, switchTenant, fetchUserProfile, refreshBranding]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
